import { useCallback, useEffect, useRef, useState } from 'react';
import { Location } from 'history';
import { UnsavedChangesGuardProps as BaseUnsavedChangesGuardProps } from './types';
import { useBlocker } from '../../services/hooks/use-unsaved-changes-warning/useBlocker';
import { withRouter, WithRouterProps } from '../../services/hooks/with-router/withRouter';

type UnsavedChangesGuardProps = BaseUnsavedChangesGuardProps & WithRouterProps;

const UnsavedChangesGuard = ({
  when,
  shouldBlockNavigation,
  location,
  showDialog,
  isVisible,
  next
}: UnsavedChangesGuardProps) => {
  const [visibleDialog, setVisibleDialog] = useState(false);
  const targetLocation = useRef<Location | null>(null);
  const blockedTransaction = useRef<unknown>(null);
  const isClosingRef = useRef(false);
  const isNavigatingRef = useRef(false);
  const dialogShownRef = useRef(false);

  const onConfirm = useCallback(() => {
    if (blockedTransaction.current) {
      // Set navigation flag to prevent blocking the navigation we're about to make
      isNavigatingRef.current = true;
      
      // Call the next prop when user confirms they want to leave
      if (next && targetLocation.current) {
        next(targetLocation.current);
      } else {
        // Fallback to retry if next prop is not provided
        (blockedTransaction.current as any).retry();
      }
      blockedTransaction.current = null;
      targetLocation.current = null;
      setVisibleDialog(false);
      isClosingRef.current = false;
    }
  }, [next]);

  // Show dialog when needed
  useEffect(() => {
    if (visibleDialog && !isVisible && !isClosingRef.current && !dialogShownRef.current) {
      dialogShownRef.current = true; // Mark dialog as shown
      showDialog(onConfirm);
    }
  }, [visibleDialog, isVisible, showDialog, onConfirm]);

  // Sync with external dialog state
  useEffect(() => {
    setVisibleDialog(isVisible);
    
    // If dialog is closed, reset flags
    if (!isVisible) {
      dialogShownRef.current = false; // Reset dialog shown flag
      if (isNavigatingRef.current) {
        isNavigatingRef.current = false;
      }
    }
  }, [isVisible]);

  // Replace <Prompt> logic with useBlocker
  useBlocker(tx => {
    const nextLocation = tx.location as Location;

    // If we're in the middle of a confirmed navigation, allow it
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false; // Reset the flag
      (tx as any).retry(); // allow the transition
      return;
    }

    if (shouldBlockNavigation(nextLocation) && location.pathname !== nextLocation.pathname) {
      blockedTransaction.current = tx;
      targetLocation.current = nextLocation;
      // Reset flags for new navigation attempt
      isNavigatingRef.current = false;
      dialogShownRef.current = false;
      setVisibleDialog(true);
      isClosingRef.current = false;
      // Do not proceed — wait for confirmation
    } else {
      (tx).retry(); // allow the transition
    }
  }, when);

  return null;
};

export default withRouter(UnsavedChangesGuard);
